// Error / network / RLS classifiers for the offline queue.
//
// Pure string predicates that decide what happens to a failed write:
//   - isNetworkish  → transient, push to the queue / keep retrying
//   - isAuthTransient → expired JWT right after reconnect → same handling as
//                     isNetworkish (queue / back off); refreshes within moments
//   - isPermanent4xx → payload fault that will NEVER succeed → mark failed (no loop)
//   - isRlsDenial   → permission/tenant denial → friendly ValidationError on the
//                     live path; treated as transient (retried) on the drain path
//
// Extracted from offlineQueue.ts (Phase 3b, step 1) so this retry/drop decision
// logic is unit-testable in isolation. Keep it dependency-free and pure.

// Probe override (idle-wake audit 6.2/P2.8): on some fleet devices
// navigator.onLine lies FALSE chronically (VPN/virtual NIC), which used to
// strand queued WRITES too — the drain gated on the same lying signal as the
// paused reads. When the reconciler's cache-busted probe gets ANY HTTP
// response it calls markLinkProven(); for the next PROBE_TRUST_MS the drain
// trusts the probe over navigator.onLine. Never the other direction — a probe
// failure must not force offline (navigator.onLine === true stays trusted).
const PROBE_TRUST_MS = 30_000
let linkProvenAt = 0
export function markLinkProven(): void {
  linkProvenAt = Date.now()
}

export function offline(): boolean {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return false
  return Date.now() - linkProvenAt >= PROBE_TRUST_MS
}

export function isNetworkish(msg: string): boolean {
  const m = msg.toLowerCase()
  return m.includes('failed to fetch')
      || m.includes('networkerror')
      || m.includes('network error')
      || m.includes('load failed')
      || m.includes('timeout')
      || m.includes('offline')
}

// Council T3.8. A permanent payload fault — the same row will NEVER succeed
// no matter how many times we retry (duplicate key, FK/check violation,
// bad input). These get marked failed immediately so we don't loop. We
// deliberately EXCLUDE permission/jwt/denied: those are usually transient
// (RLS denial during a tenant-switch race, expired session) and should be
// retried, not killed.
// The refusals receive_packed_pallets raises with 42501 (mig 20261048200000).
// Each needs a person (a manager, an administrator, a correct clock), so a
// retry under the same sign-in can never pass.
const RECEIVE_REFUSALS = [
  'an older pack date needs',
  'an older received date needs',
  'receiving pallets needs the manager',
  'the received date cannot be in the future',
]

export function isPermanent4xx(msg: string, code?: string | null): boolean {
  // SQLSTATE wins when the caller has it: a trigger that RAISEs a custom
  // message (e.g. the mig-822 blueberry tip guard) carries ERRCODE 23514 but
  // none of the substrings below — without the code check the drain retried it
  // to MAX_DRAIN_ATTEMPTS before surfacing. 23505 (unique) is deliberately NOT
  // listed: the drain treats it as idempotent-replay success upstream.
  if (code && ['23514', '23502', '23503', '22P02'].includes(code)) return true
  const m = msg.toLowerCase()
  // receive_packed_pallets refuses an older pack date from a user without the
  // back-date permission (mig 20261048200000, SQLSTATE 42501). The same capture
  // fails again until a manager drains it, so it must not loop to the retry cap.
  if (code === '42501' && RECEIVE_REFUSALS.some(r => m.includes(r))) return true
  return m.includes('duplicate key')
      || m.includes('violates unique')
      || m.includes('violates foreign key')
      || m.includes('violates check')
      || m.includes('invalid input syntax')
      || m.includes('null value in column')
}

// FM07 UF-18. The refusals post_ppe_issue raises that a retry under the same
// sign-in can never pass. 22023 is the RPC's own invalid-parameter code (no
// such worker, no such item, no quantity, a missing signature); the two 42501
// refusals need a person with another role, or a share put back. Everything
// else it raises is transient, except an over-issue: the stock trigger raises
// P0001 'Insufficient stock for "..."' with no useful code, so it is matched
// on its words (any code). 42501 is NOT listed whole: "requires an active
// tenant" is a sign-in race that heals.
const PPE_ROLE_REFUSALS = [
  'only admins or managers can issue ppe',
  'this item is not shared with your tenant',
]
export function isPpeRefusal(msg: string, code?: string | null): boolean {
  if (code === '22023') return true
  const m = msg.toLowerCase()
  if (m.includes('insufficient stock for')) return true
  return code === '42501' && PPE_ROLE_REFUSALS.some(r => m.includes(r))
}

// An expired / not-yet-refreshed JWT on a write — the NORMAL state for the
// first seconds after a long-offline device reconnects. Field case that found
// it (2026-06-12): a scout's token expired mid-walk; back in signal they tapped
// "End walk", the live insert went out with the expired token (useSaveDb's
// snapshot client has autoRefreshToken:false and the main client hadn't
// refreshed yet) → PostgREST 401 "JWT expired" → a hard "Walk save failed"
// alert instead of queueing. The token refreshes moments later, so this is
// queue-and-retry territory, never an operator-facing error. Deliberately
// narrow: RLS/permission denials are isRlsDenial; payload faults are
// isPermanent4xx.
export function isAuthTransient(msg: string): boolean {
  const m = msg.toLowerCase()
  return m.includes('jwt expired')
      || m.includes('jwt is expired')
      || m.includes('token is expired')
      || m.includes('invalid jwt')
      || m.includes('pgrst301')
}

// An RLS / permission denial — the caller's role (or their session's tenant)
// isn't allowed to write this row. On the LIVE submit path this is an operator
// /admin setup issue, not an engineering bug, so we surface it as a friendly
// ValidationError (shows in the form AND, per errorReporter, is NOT paged to
// Sentry). NOT used on the drain path: there an RLS denial is treated as
// transient (tenant-switch race) and retried — see isPermanent4xx's comment.
export function isRlsDenial(msg: string): boolean {
  const m = msg.toLowerCase()
  return m.includes('row-level security')
      || m.includes('violates row-level')
      || m.includes('permission denied')
}

// A server-busy / rate-limit / retryable-infra fault (W5, offline audit). The
// SAME write will likely succeed once the server recovers or the rate-limit
// window rolls over, so it's TRANSIENT — the drain must back off and retry, not
// mark it failed and (worse) not keep hammering a struggling server. Distinct
// from isNetworkish (the link itself is down) and isPermanent4xx (bad payload).
//
// Signals (code preferred, message as fallback):
//   - PostgREST maps SQLSTATE 'PTxyz' -> HTTP status xyz, so the app's own
//     per-tenant rate limiter (RAISE SQLSTATE 'PT429', mig 20260701220500) and
//     any 'PT5xx' arrive with code 'PT429' / 'PT5nn'.
//   - Postgres transient codes: 57014 (statement timeout / canceled), 53300
//     (too many connections), 40001 (serialization failure), 40P01 (deadlock),
//     57P03 (cannot connect now), 08xxx (connection exceptions).
//   - message fallbacks for CDN/gateway 5xx + rate-limit text (used when the
//     code was dropped upstream, e.g. a helper that re-threw before W8's fix).
// Mirrors the 429/5xx-are-transient stance already taken in faceClockQueue.ts
// and authRefresh.ts, kept in one predicate here.
export function isServerBusy(msg: string, code?: string | null): boolean {
  if (code) {
    const c = code.toUpperCase()
    if (c === 'PT429' || /^PT5\d\d$/.test(c)) return true
    if (['57014', '53300', '40001', '40P01', '57P03', '08000', '08003', '08006'].includes(c)) return true
  }
  const m = msg.toLowerCase()
  return m.includes('rate_limited')
      || m.includes('rate limit')
      || m.includes('too many requests')
      || m.includes('statement timeout')
      || m.includes('canceling statement')
      || m.includes('service unavailable')
      || m.includes('bad gateway')
      || m.includes('gateway timeout')
      || m.includes('temporarily unavailable')
}

// The app's rate limiter has no Retry-After HEADER (it's a PostgREST-mapped
// RAISE), but its message embeds a hint — "…Retry after ~45 s." — which is the
// only server-provided delay signal. Returns the hint in ms, or null. The drain
// backoff uses it as a floor so a 429 waits at least as long as the server asked.
export function retryAfterMs(msg: string): number | null {
  const m = /retry after ~?\s*(\d+)\s*s/i.exec(msg)
  return m ? Number(m[1]) * 1000 : null
}

// The last_error the offline-queue badge shows an OPERATOR when a write is
// permanently stranded. For most strands the raw DB message is fine, but a
// piece-work drop stranded on save_pick_tallies' 'Worker X not found in current
// tenant' (mig 804) reads as opaque tenant jargon to a foreman — when the real
// cause is that the worker was RENUMBERED or removed after the drop was captured
// offline under the old number (see the move-worker recipe: a worker_number
// change orphans any offline-queued write keyed on the old number). The drop can
// only be recovered by re-capturing under the current number, so spell that out.
// Returns null when the raw message should stand. The raw error still goes to
// Sentry — this is operator guidance, not the engineering signal.
// KF49 2026-07-22: F329 -> ANC329 stranded orchard drops on field devices.
export function operatorStrandMessage(kind: string, msg: string): string | null {
  // A PPE issue the server refused. The item was already handed over and both
  // people signed, so Clear (which throws the signatures away) is the LAST
  // resort; say what to try first.
  if (kind === 'ppe_issue') {
    const why = msg.replace(/[.\s]+$/, '')
    return `The PPE was handed over but the server has not recorded it: ${why}. `
         + 'Fix the cause (stock, role or worker), then tap Retry. '
         + 'Tap Discard only if you will issue it again, because Discard deletes both signatures.'
  }
  const m = /Worker (\S+) not found in current tenant/.exec(msg)
  if (m && kind === 'pick_tallies') {
    return `This drop is for worker ${m[1]}, who has been renumbered or removed since it was `
         + `captured. Re-capture the drop under the worker's current number, then Clear this.`
  }
  // The card was live when it was scanned offline, and stopped by the time the
  // drop synced — saves re-check the badge at SAVE time (save_pick_tallies,
  // save_packout_drops). Since mig 20261039300000 the usual cause is the
  // nightly stop a week after the worker's end date, and a wrong end date is
  // fixed on the worker's record, after which Retry saves the drop as captured.
  // Clear is the one wrong move: it throws the count away. (Review, 6 Oct.)
  if ((kind === 'pick_tallies' || kind === 'packout_drops') && /requires a live badge/i.test(msg)) {
    return 'This drop was scanned on a card that has since been stopped. If the worker still works '
         + 'here and their end date is wrong, correct it on their record, then tap Retry: the drop saves '
         + 'as captured. If the card was replaced or reported lost, capture the drop again by picking the '
         + 'worker from the list, then Clear this. Clearing it first loses the count.'
  }
  // An offline receipt the server refused. Retry works once the right person
  // is signed in on this device; Clear throws the receipt away.
  if (kind === 'pallet_receipt' && /an older (pack|received) date needs/i.test(msg)) {
    return 'This receipt has a pack date or received date older than your role may enter. '
         + 'Ask a manager to sign in on this device and tap Retry: the receipt saves as captured. '
         + 'Do not tap Clear first: it throws the receipt away.'
  }
  if (kind === 'pallet_receipt' && /receiving pallets needs the manager/i.test(msg)) {
    return 'Your role cannot receive pallets. Ask an administrator, or ask a Manager or Scale operator '
         + 'to sign in on this device and tap Retry. Do not tap Clear first: it throws the receipt away.'
  }
  if (kind === 'pallet_receipt' && /received date cannot be in the future/i.test(msg)) {
    return 'This receipt carries a received date in the future. Check the date and time on this device, '
         + 'then capture the receipt again and tap Clear on this one.'
  }
  return null
}
